import { Injectable } from '@angular/core';
import { IS_DEVELOPMENT, DETECTION_API_SECRET, DETECTION_EXPIRATION_TIME } from '@root/app.config';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { STORAGE_NAMES } from '@root/app.config';
import {
	DetectionApiResponse,
	DetectionPoint,
	DetectionResult,
	ImageSize,
	PostDataPayload,
	PresignedUrlResponse,
} from '@root/app.interfaces';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';

@Injectable({
	providedIn: 'root',
})
export class DetectionService {
	constructor(
		private shareService: ShareService,
		private sessionStorageService: SessionStorageService,
		private indexedDBService: IndexedDBService,
	) {}

	private currentPollId = 0;
	private lastFile: File | null = null;

	private API_BASE_URL: object = {
		dev: 'https://ziptrak-ai.ddos.la',
		//dev: 'http://127.0.0.1:8000',
		prod: 'https://detect.ziptrak.com',
	};

	public API_PATH: object = {
		systemActivate: '/system/activate',
		getPresignedUrl: '/file/get_presigned_url',
		taskSubmit: '/task/submit',
		taskQuery: '/task/query',
	};

	/**
	 * Given an API name, returns the full URL for that API.
	 *
	 * @param {string} apiPath the path of the API to get the URL for
	 * @returns {string} the full URL for the API
	 * @throws {Error} if the API name is not found in the API_PATH object
	 */
	private getApiUrl(apiPath: string): string {
		let apiBaseUrl: string;

		if (IS_DEVELOPMENT()) {
			apiBaseUrl = this.API_BASE_URL['dev'];
		} else {
			apiBaseUrl = this.API_BASE_URL['prod'];
		}

		return apiBaseUrl + apiPath;
	}

	/**
	 * Send a POST request to the given URL with the given data.
	 *
	 * Automatically appends a token to the data, which is generated using the
	 * {@link generateToken} method.
	 *
	 * @param {string} url the URL to send the request to
	 * @param {object} data the data to send in the request body
	 * @returns {Promise<DetectionApiResponse>} a Promise that resolves to the response data
	 * @throws {Error} if the response does not have a 'status' field, or if the status is not 1
	 */
	private async postData<T>(url: string, data: PostDataPayload): Promise<DetectionApiResponse<T>> {
		const postData = {
			...data,
			token: await this.generateToken(),
		};

		const response = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(postData),
		});

		const result = await response.json();

		if (!Object.prototype.hasOwnProperty.call(result, 'status')) {
			throw new Error(`Response missing status field, ${result}`);
		}

		if (result.status != 1) {
			throw new Error(result.msg);
		}

		return result;
	}

	private async generateToken(): Promise<string> {
		const secret = DETECTION_API_SECRET;
		const timestamp = Math.floor(Date.now() / 1000); // Get current timestamp in seconds

		// Convert secret and timestamp to Uint8Array
		const encoder = new TextEncoder();
		const keyData = encoder.encode(secret); // Encode secret
		const messageData = encoder.encode(timestamp.toString()); // Encode timestamp as string // changed from original to include .toString();

		// Import the key for HMAC-SHA512
		const key = await window.crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: { name: 'SHA-512' } }, false, ['sign']);

		// Generate the HMAC signature
		const signature = await window.crypto.subtle.sign('HMAC', key, messageData);

		// Convert the signature to a lowercase hex string (to match Python hexdigest())
		const token = Array.from(new Uint8Array(signature))
			.map((b) => b.toString(16).padStart(2, '0')) // Ensures lowercase hex output
			.join('');

		return token;
	}

	/**
	 * Activates the system by sending a POST request to the system activation API.
	 * Must call this function immediately when users enter DYB as initialize the AI Detection system would take few seconds (up to 40s).
	 *
	 * @returns {Promise<DetectionApiResponse>} A promise that resolves to the response data of the activation request.
	 * @throws {Error} If the response does not have a 'status' field, or if the status is not 1.
	 */
	async activateSystem() {
		const url: string = this.getApiUrl(this.API_PATH['systemActivate']);

		return await this.postData(url, {});
	}

	onLoadImage(file: File | Blob): Promise<ImageSize> {
		return new Promise((resolve, reject) => {
			const image = new Image();
			image.src = window.URL.createObjectURL(file);
			image.onload = () => {
				window.URL.revokeObjectURL(image.src);
				resolve({
					height: image.height,
					width: image.width,
				});
			};
			image.onerror = (error) => reject(error);
		});
	}

	retryLast(): void {
		if (this.lastFile) {
			void this.startDetection(this.lastFile);
		} else {
			const ivBackgroundImage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_uploaded_image_visualisation_background);

			if (ivBackgroundImage) {
				fetch(ivBackgroundImage.cropped_src)
					.then((res) => res.blob())
					.then((blob) => {
						this.startDetection(blob);
					});
			} else {
				// TODO: Throw error?
			}
		}
	}

	/**
	 * Uploads a file and submits detection tasks.
	 *
	 * @param {Blob} file The file to upload.
	 */
	async startDetection(file): Promise<void> {
		try {
			this.lastFile = file;
			const hash = await this.calculateFileHash(file);
			this.currentPollId++; // Cancels any running poll
			this.shareService.setDetectionDone(false);
			this.sessionStorageService.removeData(STORAGE_NAMES.zip_openings_data);
			this.shareService.setDetectionStart(); // Tell other compoenents that the detection has started

			const presignedResponse = await this.retry(() => this.getPresignedUrl(file.type, hash), 3);

			// If this image has already been uploaded, don't upload it again
			if (!presignedResponse.file_already_uploaded) {
				await this.retry(() => this.uploadFile(file, (presignedResponse as { url: string }).url), 3);
			}

			const outdoorUUID = await this.retry(() => this.taskSubmit((presignedResponse as { key: string }).key, 0, hash), 3);
			const indoorUUID = await this.retry(() => this.taskSubmit((presignedResponse as { key: string }).key, 1, hash), 3);
			const imgData = await this.onLoadImage(file);

			this.pollBothTasks(indoorUUID, outdoorUUID, this.currentPollId, imgData);
		} catch (error) {
			// console.error('startDetection failed, FAIL OVER IN PLACE:', error);
			this.shareService.setDetectionError(error);
			this.shareService.setDetectionDone(true);
			// TODO: REMEMBER TO REMOVE BELOW IN PRODUCTION
			// const imgData = await this.onLoadImage(file);
			// this.pollBothTasks('mockIndoorUUID', 'mockOutdoorUUID', this.currentPollId, imgData);
		}
	}

	/**
	 * Generic retry helper with optional back‑off.
	 * @param fn The async function to run.
	 * @param retries How many times to retry (defaults to 3 total attempts).
	 * @param delay Delay (ms) before each retry – increasing 50% each time here.
	 */
	async retry<T>(fn: () => Promise<T>, retries = 3, delay = 300): Promise<T> {
		for (let attempt = 1; attempt <= retries; attempt++) {
			try {
				return await fn();
			} catch (err) {
				if (attempt === retries) {
					throw err;
				} // bubble up after final failure
				await new Promise((res) => setTimeout(res, delay));
				delay = Math.round(delay * 1.5); // simple back‑off
			}
		}

		throw new Error('Unreachable'); // This line is never reached, but satisfies TypeScript’s control‑flow analysis
	}

	/**
	 * Retrieves a presigned URL for uploading a file to AWS S3.
	 *
	 * @param {string} fileType - The MIME type of the file for which the presigned URL is requested.
	 * @param {string} hash - The hash (sha-256) of the file to submit for detection.
	 * @returns {Promise<PresignedUrlResponse>} A promise that resolves to an object containing the presigned URL and any additional data required for the upload.
	 * @throws {Error} If the post request fails or the response is invalid.
	 */

	private async getPresignedUrl(fileType: string, hash: string): Promise<PresignedUrlResponse> {
		const url: string = this.getApiUrl(this.API_PATH['getPresignedUrl']);
		const result = await this.postData<PresignedUrlResponse>(url, {
			file_type: fileType,
			sha256: hash,
		});

		return result.data;
	}

	/**
	 * Uploads a file to the given URL. Normally, it should be a AWS S3 presigned url.
	 *
	 * @param {Blob} file the file to upload
	 * @param {string} url the URL to upload the file to
	 * @returns {Promise<boolean>} a Promise that resolves to true on success, or rejects with an Error on failure
	 */
	private async uploadFile(file: Blob, url: string): Promise<boolean> {
		const response = await fetch(url, {
			method: 'PUT',
			body: file,
			headers: { 'Content-Type': file.type },
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return true;
	}

	/**
	 * Submits a detection task for the uploaded file.
	 *
	 * @param {string} fileKey The key of the file to submit for detection.
	 * @param {number} [isIndoor=1] Specifies whether the image is of an indoor scene.
	 * @param {string} [hash] The hash (sha-256) of the file to submit for detection.
	 * @returns {Promise<string>} A Promise that resolves to the UUID of the submitted task.
	 * @throws {Error} If the post request fails or the response is invalid.
	 */
	private async taskSubmit(fileKey: string, isIndoor: number = 1, hash?: string): Promise<string> {
		const url: string = this.getApiUrl(this.API_PATH['taskSubmit']);
		const result: any = await this.postData(url, {
			type: 'image',
			file_key: fileKey,
			additional_parameter: {
				is_indoor: isIndoor,
			},
			sha256: hash,
		});

		// task UUID
		return result.data.task_uuid;
	}

	/**
	 * Queries the task with the given UUID.
	 *
	 * @param {string} UUID the UUID of the task to query
	 * @returns {Promise<DetectionResult>} a Promise that resolves to the result of the task
	 */
	private async taskQuery(UUID: string): Promise<DetectionResult> {
		const url: string = this.getApiUrl(this.API_PATH['taskQuery']);
		const result: any = await this.postData(url, {
			task_uuid: UUID,
		});

		return this.toPointObjects(result.data.coordinate_list);
	}

	/**
	 * Queries the task with the given UUID and waits for it to complete with a maximum timeout of
	 * {@link DETECTION_EXPIRATION_TIME} milliseconds. The query is retried every 100ms until the timeout
	 * is reached.
	 *
	 * @param {string} UUID the UUID of the task to query
	 * @returns {Promise<DetectionResult | 0>} a Promise that resolves to the result of the task, or null if the
	 * task timed out
	 */
	private async pollTaskQuery(UUID: string, pollId: number): Promise<DetectionResult | 0> {
		let keepPolling = true;

		const timeoutPromise = new Promise<0>((resolve) => {
			setTimeout(() => {
				keepPolling = false;
				resolve(0);
			}, DETECTION_EXPIRATION_TIME * 1000);
		});

		const queryPromise = new Promise<DetectionResult | 0>((resolve) => {
			(async () => {
				while (keepPolling && pollId === this.currentPollId) {
					try {
						const result = await this.taskQuery(UUID);
						if (pollId === this.currentPollId) {
							resolve(result);
							return;
						} else {
							// Aborted mid-flight
							resolve(0);
							return;
						}
					} catch (error) {
						await new Promise((res) => setTimeout(res, 500));
					}
				}
				resolve(0); // timeout or poll ID mismatch
			})();
		});

		return Promise.race([timeoutPromise, queryPromise]);
	}

	private async pollBothTasks(indoorUUID: string, outdoorUUID: string, pollId: number, imgData: ImageSize): Promise<void> {
		const [indoorResult, outdoorResult] = await Promise.all([
			this.pollTaskQuery(indoorUUID, pollId),
			this.pollTaskQuery(outdoorUUID, pollId),
			// this.mock2PollTaskQuery(indoorUUID, pollId),
			// this.mock2PollTaskQuery(outdoorUUID, pollId),
		]);

		if (pollId !== this.currentPollId) {
			console.warn('Detection tasks superseded by newer image detection tasks. Discarding results.');
			this.shareService.setDetectionDone(false);
			this.sessionStorageService.removeData(STORAGE_NAMES.zip_openings_data);
			return;
		}

		const results = this.filterDetectionResults(indoorResult, outdoorResult, imgData);

		if (!results) {
			const error = new Error('No openings detected');
			this.shareService.setDetectionError(error);
		}

		if (indoorResult === 0 || outdoorResult === 0) {
			console.warn('One or both detection tasks timed out.');
			this.sessionStorageService.setDetectionResults(
				{
					results,
					timeout: true,
					imageSize: { width: imgData.width, height: imgData.height },
				},
				STORAGE_NAMES.zip_detection_results,
			);

			if (indoorResult === 0 && outdoorResult === 0) {
				const error = new Error('Both detection tasks timed out');
				this.shareService.setDetectionError(error);
			}

			this.shareService.setDetectionDone(true);
			return;
		}

		this.sessionStorageService.setDetectionResults(
			{
				results,
				timeout: false,
				imageSize: { width: imgData.width, height: imgData.height },
			},
			STORAGE_NAMES.zip_detection_results,
		);

		this.shareService.setDetectionDone(true);
	}

	toPointObjects(data: number[][][]): DetectionResult {
		return data.map((quad) => quad.map(([x, y]) => ({ x, y })));
	}

	private async mock1PollTaskQuery(UUID: string, pollId: number): Promise<DetectionResult | 0> {
		return new Promise((resolve) => {
			setTimeout(() => {
				if (pollId !== this.currentPollId) {
					console.log('Mock poll cancelled or outdated');
					resolve(0);
					return;
				}
				const canvas = document.getElementById('renderCanvas');

				const width = canvas.clientWidth;
				const height = canvas.clientHeight;
				resolve([
					[
						{ x: width / 2 - 100, y: height / 2 - 100 },
						{ x: width / 2 + 100, y: height / 2 - 100 },
						{ x: width / 2 + 100, y: height / 2 + 100 },
						{ x: width / 2 - 100, y: height / 2 + 100 },
					],
					[
						{ x: width / 2 - 200, y: height / 2 - 200 },
						{ x: width / 2 - 50, y: height / 2 - 200 },
						{ x: width / 2 - 50, y: height / 2 - 50 },
						{ x: width / 2 - 200, y: height / 2 - 50 },
					],
				]);
			}, 2000);
		});
	}

	private async mock2PollTaskQuery(UUID: string, pollId: number): Promise<DetectionResult | 0> {
		return new Promise((resolve) => {
			setTimeout(() => {
				if (pollId !== this.currentPollId) {
					console.log('Mock poll cancelled or outdated');
					resolve(0);
					return;
				}

				resolve(null);
			}, 2000);
		});
	}

	private filterDetectionResults(indoor: DetectionResult | 0, outdoor: DetectionResult | 0, imgData: ImageSize): DetectionResult | null {
		// TODO: make this actually filter the results
		const isValidOpening = (quad: DetectionPoint[]): boolean => {
			const dist = (p1: DetectionPoint, p2: DetectionPoint): number => Math.hypot(p1.x - p2.x, p1.y - p2.y);

			const [p0, p1, p2, p3] = quad;

			const left = dist(p0, p3);
			const right = dist(p1, p2);
			const top = dist(p0, p1);
			const bottom = dist(p2, p3);

			const isVerticalValid = left >= right / 4 && right >= left / 4;
			const isHorizontalValid = top >= bottom / 4 && bottom >= top / 4;

			if (!isVerticalValid || !isHorizontalValid) {
				return false;
			}

			const isWidthValid = top < imgData.width * 0.99 && bottom < imgData.width * 0.99;
			const isHeightValid = left < imgData.height * 0.99 && right < imgData.height * 0.99;

			return isWidthValid || isHeightValid;
		};

		let finalList: DetectionResult = [];

		if (Array.isArray(indoor) && indoor.length > 0) {
			finalList = finalList.concat(indoor.filter(isValidOpening));
		}

		if (Array.isArray(outdoor) && outdoor.length > 0) {
			finalList = finalList.concat(outdoor.filter(isValidOpening));
		}

		return finalList.length > 0 ? finalList : null;
	}

	/**
	 * Calculates the SHA-256 hash of a file
	 * @param {Blob} file
	 * @returns
	 */
	private async calculateFileHash(file: Blob): Promise<string> {
		const arrayBuffer = await file.arrayBuffer();
		const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		const hashHex = hashArray
			.map((b) => b.toString(16).padStart(2, '0'))
			.join('')
			.toLowerCase();

		return hashHex;
	}
}
